import React from "react";
import { createPortal } from "react-dom";
import { VisuallyHidden } from "../../atoms/VisuallyHidden";
import { Toast } from "../Toast";
import { CenterList, CenterRegion, List, Pill, Pills, Region } from "./ToastStack.styles";
import type { ToastStackCentered, ToastStackItem, ToastStackProps } from "./ToastStack.types";

const LIST_ID = "toast-stack-list";

/** The app-wide stack of toasts (prototype `.nt-stack`) with its live regions. */
export const ToastStack: React.FC<ToastStackProps> = ({
  label,
  toasts,
  hiddenCount,
  expanded,
  moreLabel,
  moreAriaLabel,
  fewerLabel,
  dismissAllLabel,
  onToggleExpanded,
  onDismissAll,
  onAction,
  onSecondaryAction,
  centered = [],
  onDismiss,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
  onKeyDown,
  lift = 0,
  polite,
  assertive,
  stackRef,
  className,
}) => {
  const renderToast = (toast: ToastStackItem) => (
    <Toast
      {...toast}
      onAction={toast.actionLabel ? () => onAction(toast.id) : undefined}
      onSecondaryAction={toast.secondaryActionLabel && onSecondaryAction ? () => onSecondaryAction(toast.id) : undefined}
      onDismiss={() => onDismiss(toast.id)}
    />
  );
  const renderCentered = (group: ToastStackCentered) => {
    if (!group.toasts.length) return null;
    const region = (
      <CenterRegion
        key={group.key}
        aria-label={label}
        $anchored={Boolean(group.container)}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      >
        <CenterList>
          {group.toasts.map((toast) => (
            <li key={toast.id}>{renderToast(toast)}</li>
          ))}
        </CenterList>
      </CenterRegion>
    );
    return group.container ? createPortal(region, group.container, group.key) : region;
  };
  return (
  <>
    {toasts.length ? (
      <Region
        ref={stackRef}
        aria-label={label}
        className={className}
        $lift={lift}
        onPointerEnter={onPointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
      >
        {expanded ? (
          <Pills>
            <Pill type="button" aria-expanded="true" aria-controls={LIST_ID} onClick={onToggleExpanded}>
              {fewerLabel}
            </Pill>
            <Pill type="button" onClick={onDismissAll}>
              {dismissAllLabel}
            </Pill>
          </Pills>
        ) : hiddenCount > 0 ? (
          <Pills>
            <Pill type="button" aria-expanded="false" aria-controls={LIST_ID} aria-label={moreAriaLabel} onClick={onToggleExpanded}>
              {moreLabel}
            </Pill>
          </Pills>
        ) : null}
        <List id={LIST_ID} $expanded={expanded}>
          {toasts.map((toast) => (
            <li key={toast.id}>
              {renderToast(toast)}
            </li>
          ))}
        </List>
      </Region>
    ) : null}
    {centered.map(renderCentered)}
    <VisuallyHidden aria-live="polite" aria-atomic="true" data-testid="toast-live-polite">
      {polite ? <span key={polite.key}>{polite.text}</span> : null}
    </VisuallyHidden>
    <VisuallyHidden aria-live="assertive" aria-atomic="true" data-testid="toast-live-assertive">
      {assertive ? <span key={assertive.key}>{assertive.text}</span> : null}
    </VisuallyHidden>
  </>
  );
};

export default ToastStack;
